/**
 * `@tenant-theme` — apply a tenant app's brand colours to a module's Vuetify theme.
 *
 * The standalone tenant shell (`metaship-frontend/standalone/`) publishes the resolved
 * tenant on the host global state: `window.MetaShipGlobalState.state.tenant.branding`
 * (contract: docs/superpowers/specs/2026-08-27-tenant-apps-white-label-design.md,
 * Appendix A1 + A5). Every microfrontend declares its own compile-time palette in
 * `src/plugins/vuetify.ts`; this helper overlays the tenant colours at
 * `createVuetify()` time so the module matches the shell without a rebuild.
 *
 * Usage — wrap the module's existing `theme` option (ONE edit per module):
 *
 *   import { withTenantTheme } from "@tenant-theme";
 *   export const vuetify = createVuetify({
 *     …,
 *     theme: withTenantTheme({ scope, stylesheetId, defaultTheme, themes: { … } }),
 *   });
 *
 * Rules:
 * - `primary`, `secondary`, `tertiary` apply to BOTH dark and light themes
 *   (`tertiary` also fills Vuetify's `accent` slot).
 * - `background`, `surface`, `navbar` apply ONLY to the theme whose `dark` flag matches
 *   the tenant's `defaultTheme` — a tenant that brands a white background must not
 *   paint white surfaces into a user's dark mode. `surface` also fills the house
 *   aliases `card` and `foreground`; `navbar` also fills `toolbars`.
 * - Under the MetaShip shell (no `tenant` on the global) the options are returned
 *   untouched, so this is a no-op for every existing deployment.
 * - Wiring: `vite.config.ts` alias `"@tenant-theme"` → `../../shared/vue3-tenant-theme/src`
 *   and `tsconfig.json` paths `"@tenant-theme": ["../../shared/vue3-tenant-theme/src/index.ts"]`
 *   (same recipe as `@supergrid-theme`).
 */

export interface TenantBrandingColours {
  primary: string | null;
  secondary: string | null;
  tertiary: string | null;
  background: string | null;
  surface: string | null;
  navbar: string | null;
}

export interface TenantBranding {
  logoLightUrl: string | null;
  logoDarkUrl: string | null;
  iconUrl: string | null;
  colours: TenantBrandingColours;
  defaultTheme: "dark" | "light";
}

export interface TenantState {
  slug: string;
  kind: "WHITE_LABEL" | "PRODUCT";
  name: string;
  hostname: string;
  branding: TenantBranding;
}

interface GlobalStateHost {
  MetaShipGlobalState?: { state?: { tenant?: TenantState | null } };
}

/** A minimal structural view of Vuetify's `ThemeDefinition` — keeps this package free of a Vuetify import. */
export interface ThemeLike {
  dark?: boolean;
  colors?: Record<string, string>;
}

export interface ThemeOptionsLike {
  themes?: Record<string, ThemeLike>;
  [key: string]: unknown;
}

const HEX = /^#[0-9a-fA-F]{6}$/;

function hex(value: string | null | undefined): string | null {
  return typeof value === "string" && HEX.test(value.trim()) ? value.trim() : null;
}

/** The tenant published by the standalone shell, or null under the MetaShip shell. */
export function readTenant(): TenantState | null {
  if (typeof window === "undefined") return null;
  const tenant = (window as unknown as GlobalStateHost).MetaShipGlobalState?.state?.tenant;
  return tenant && typeof tenant === "object" ? tenant : null;
}

/**
 * Overlay tenant colours onto one theme's colour map. Pure — returns a new object.
 * `dark` is the theme's own flag; `defaultTheme` is the tenant's.
 */
export function overlayTenantColours(
  colors: Record<string, string>,
  dark: boolean,
  branding: TenantBranding,
): Record<string, string> {
  const c = branding.colours ?? ({} as TenantBrandingColours);
  const out: Record<string, string> = { ...colors };

  const primary = hex(c.primary);
  const secondary = hex(c.secondary);
  const tertiary = hex(c.tertiary);
  if (primary) out.primary = primary;
  if (secondary) out.secondary = secondary;
  if (tertiary) out.accent = tertiary;

  const surfacesApply = (branding.defaultTheme === "dark") === dark;
  if (surfacesApply) {
    const background = hex(c.background);
    const surface = hex(c.surface);
    const navbar = hex(c.navbar);
    if (background) out.background = background;
    if (surface) {
      out.surface = surface;
      out.card = surface;
      out.foreground = surface;
    }
    if (navbar) {
      out.navbar = navbar;
      out.toolbars = navbar;
    }
  }
  return out;
}

/**
 * Wrap a module's Vuetify `theme` option. No-op when no tenant is published.
 */
export function withTenantTheme<T extends ThemeOptionsLike>(options: T, tenant: TenantState | null = readTenant()): T {
  const branding = tenant?.branding;
  if (!branding || !options?.themes) return options;

  const themes: Record<string, ThemeLike> = {};
  for (const [name, theme] of Object.entries(options.themes)) {
    themes[name] = {
      ...theme,
      colors: overlayTenantColours(theme.colors ?? {}, theme.dark === true, branding),
    };
  }
  return { ...options, themes };
}
